import { useState } from "react";
import { useTranslation } from "react-i18next";
import AppInputNew from "@/components/Core/AppInput/AppInputNew";
import Button from "@/components/Core/Button/Button";
import { useOrganisation, useUpdateOrganisation } from "@/networking/organisation.hooks";
import { SettingsLeaf, SettingsSubsection } from "./SettingsLeafLayout";

/**
 * Salary codes. Kept as ONE leaf with a single atomic save covering all 18
 * codes — the sub-groups (Lönetyper / OB-tillägg / Övrigt) are presentation
 * only, matching the original single `submitSalaryCodes` payload.
 */
const LonekoderPage = () => {
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();
	const { mutate: updateOrganisation } = useUpdateOrganisation();

	const codes = organisation?.salarySettings.salaryCodes;

	const [hourly, setHourly] = useState(codes?.hourly || "");
	const [guaranteedSalary, setGuaranteedSalary] = useState(codes?.guaranteedSalary || "");
	const [gfl, setGfl] = useState(codes?.gfl || "");
	const [performanceSalary, setPerformanceSalary] = useState(codes?.performanceSalary || "");

	const [ob1, setOb1] = useState(codes?.ob1 || "");
	const [ob2, setOb2] = useState(codes?.ob2 || "");
	const [ob3, setOb3] = useState(codes?.ob3 || "");
	const [ob4, setOb4] = useState(codes?.ob4 || "");
	const [ob5, setOb5] = useState(codes?.ob5 || "");

	const [sickPay, setSickPay] = useState(codes?.sickPay || "");
	const [sickPayNonPaid, setSickPayNonPaid] = useState(codes?.sickPayNonPaid || "");
	const [deduction, setDeduction] = useState(codes?.deduction || "");
	const [qualifyingDeduction, setQualifyingDeduction] = useState(codes?.qualifyingDeduction || "");
	const [qualifyingDay, setQualifyingDay] = useState(codes?.qualifyingDay || "");
	const [childCare, setChildCare] = useState(codes?.childCare || "");
	const [supplement, setSupplement] = useState(codes?.supplement || "");
	const [leaveOfAbsence, setLeaveOfAbsence] = useState(codes?.leaveOfAbsence || "");
	const [vacation, setVacation] = useState(codes?.vacation || "");

	function save() {
		if (!organisation) return;
		updateOrganisation({
			salarySettings: {
				...organisation.salarySettings,
				salaryCodes: {
					hourly: hourly.trim(),
					sickPay: sickPay.trim(),
					sickPayNonPaid: sickPayNonPaid.trim(),
					deduction: deduction.trim(),
					qualifyingDeduction: qualifyingDeduction.trim(),
					childCare: childCare.trim(),
					supplement: supplement.trim(),
					qualifyingDay: qualifyingDay.trim(),
					leaveOfAbsence: leaveOfAbsence.trim(),
					ob1: ob1.trim(),
					ob2: ob2.trim(),
					ob3: ob3.trim(),
					ob4: ob4.trim(),
					ob5: ob5.trim(),
					vacation: vacation.trim(),
					guaranteedSalary: guaranteedSalary.trim(),
					gfl: gfl.trim(),
					performanceSalary: performanceSalary.trim(),
				},
			},
		});
	}

	return (
		<SettingsLeaf
			title="Lönearter & koder"
			description="Ange koder för respektive löneart. Detta måste stämma överens med koder som finns i ert lönesystem."
		>
			<SettingsSubsection title="Lönetyper">
				<div className="grid grid-cols-2 gap-4">
					<AppInputNew label="Timlön" value={hourly} onChangeText={setHourly} />
					<AppInputNew
						label="Garantilön"
						value={guaranteedSalary}
						onChangeText={setGuaranteedSalary}
					/>
					<AppInputNew label="GFL" value={gfl} onChangeText={setGfl} />
					<AppInputNew
						label="Prestationslön"
						value={performanceSalary}
						onChangeText={setPerformanceSalary}
					/>
				</div>
			</SettingsSubsection>

			<SettingsSubsection title="OB-tillägg">
				<div className="grid grid-cols-2 gap-4">
					<AppInputNew label="OB1" value={ob1} onChangeText={setOb1} />
					<AppInputNew label="OB2" value={ob2} onChangeText={setOb2} />
					<AppInputNew label="OB3" value={ob3} onChangeText={setOb3} />
					<AppInputNew label="OB4" value={ob4} onChangeText={setOb4} />
					<AppInputNew label="OB5" value={ob5} onChangeText={setOb5} />
				</div>
			</SettingsSubsection>

			<SettingsSubsection title="Övrigt">
				<div className="grid grid-cols-2 gap-4">
					<AppInputNew label="Sjuklön" value={sickPay} onChangeText={setSickPay} />
					<AppInputNew
						label="Sjuklön utan lön"
						value={sickPayNonPaid}
						onChangeText={setSickPayNonPaid}
					/>
					<AppInputNew label="Avdrag" value={deduction} onChangeText={setDeduction} />
					<AppInputNew
						label="Karensavdrag"
						value={qualifyingDeduction}
						onChangeText={setQualifyingDeduction}
					/>
					<AppInputNew label="Karensdag" value={qualifyingDay} onChangeText={setQualifyingDay} />
					<AppInputNew label="VAB" value={childCare} onChangeText={setChildCare} />
					<AppInputNew label="Tillägg" value={supplement} onChangeText={setSupplement} />
					<AppInputNew
						label="Tjänstledighet"
						value={leaveOfAbsence}
						onChangeText={setLeaveOfAbsence}
					/>
					<AppInputNew label="Semester" value={vacation} onChangeText={setVacation} />
				</div>
			</SettingsSubsection>

			<div>
				<Button onClick={save}>{t("SAVE")}</Button>
			</div>
		</SettingsLeaf>
	);
};

export default LonekoderPage;
